import './results-by-date-dialog.less';

import clsx from 'clsx';
import { Map } from 'immutable';
import moment from 'moment';
import { dispatch, useReframe } from 'nike-re-framejs';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useMemo, useState } from 'react';

import useScrollIntoView from '@/_hooks/useScrollIntoView';
import { fireGAEvent } from '@/analytics';
import RadioButton from '@/common/components/RadioButton';
import i18n from '@i18n';
import { DialogBody, DialogTitle } from '@/ui/dialogs/Dialog';
import PopupNg from '@/ui/PopupNg';
import { INTERVALS } from './byDate';
import { changePrematchByDate } from '@/nikeone/handlers';

function setByDate(value) {
    const interval = INTERVALS[value];
    fireGAEvent('Ponuka', interval ? interval.trackValue : 'Vlastny rozsah', 'Kalendar');

    dispatch(changePrematchByDate(value));
    setTimeout(PopupNg.dispatcher.close, 500);
}

function ByDateDialog() {
    const { derefSub } = useReframe();
    const [selectedByDate, setSelectedByDate] = useState(() => derefSub(['nikeone/filter/byDate']) || 'ALL');
    const bettingDays = derefSub(['nikeone.bettingDays']) || Map();

    function handleChange(selected) {
        setSelectedByDate(selected);
        setByDate(selected);
    }

    return (
        <>
            <DialogTitle className="by-date-dialog-title">
                <h2>{i18n('filter.title.offer.time')}</h2>
            </DialogTitle>
            <DialogBody className="by-date-dialog-body">
                <div className="by-date-dialog">
                    <div className="by-date-dialog-left-column">
                        <RadioButton active={selectedByDate === 'ALL'} onClick={() => handleChange('ALL')}>
                            {INTERVALS['ALL'].text}
                        </RadioButton>
                        <div className="filter-separator" />
                        <RadioButton active={selectedByDate === 'TODAY'} onClick={() => handleChange('TODAY')}>
                            {INTERVALS['TODAY'].text}
                        </RadioButton>
                        <RadioButton active={selectedByDate === 'TOMORROW'} onClick={() => handleChange('TOMORROW')}>
                            {INTERVALS['TOMORROW'].text}
                        </RadioButton>
                        <RadioButton
                            active={selectedByDate === 'TODAY_AND_TOMORROW'}
                            onClick={() => handleChange('TODAY_AND_TOMORROW')}
                        >
                            {INTERVALS['TODAY_AND_TOMORROW'].text}
                        </RadioButton>
                        <div className="filter-separator" />
                        <RadioButton active={selectedByDate === 'HOUR'} onClick={() => handleChange('HOUR')}>
                            {INTERVALS['HOUR'].text}
                        </RadioButton>
                        <RadioButton active={selectedByDate === 'HOURS3'} onClick={() => handleChange('HOURS3')}>
                            {INTERVALS['HOURS3'].text}
                        </RadioButton>
                    </div>
                </div>
                <div className="filter-separator" />
                <div className="betting-days-slider-fix">
                    <BettingDaysSlider selectedByDate={selectedByDate} bettingDays={bettingDays} />
                </div>
            </DialogBody>
        </>
    );
}

export default ByDateDialog;

const BettingDaysSlider = function({ selectedByDate, bettingDays }) {
    const scrollElementRef = useScrollIntoView(20);

    //za poslednym dnom vysledkov zobrazime separator
    const today = useMemo(() => moment(), []);
    const lastPastDay = useMemo(
        function getLastPast() {
            return bettingDays.findLastKey(day => day.isSameOrBefore(today));
        },
        [today, bettingDays]
    );

    if (bettingDays.isEmpty()) {
        return null;
    }
    // ak nie je ziadny aktivny, tak scroll ide na dnes
    const hasActive = bettingDays.has(selectedByDate);
    const scroll = hasActive ? selectedByDate : INTERVALS.TODAY.date[0];
    return (
        <div className="body-scroll-lock-ignore">
            <div className="betting-days-slider">
                {bettingDays
                    .map((dayMoment, day) => (
                        <React.Fragment key={day}>
                            <BettingDay
                                byDate={day}
                                dayMoment={dayMoment}
                                type={dayMoment.isSameOrBefore(today) ? 'past' : 'future'}
                                selected={selectedByDate === day}
                                scrollElementRef={scroll === day ? scrollElementRef : null}
                            />
                            {day === lastPastDay ? <div className="betting-days-separator" /> : null}
                        </React.Fragment>
                    ))
                    .toList()}
            </div>
        </div>
    );
};

const BettingDay = memo(function BettingDay({ selected, type, byDate, dayMoment, scrollElementRef = null }) {
    const day = dayMoment.day();
    const classes = clsx('betting-day', type, { weekend: day === 6 || day === 0, selected });

    const handleClick = useCallback(
        function handleClick() {
            setByDate(byDate);
        },
        [byDate]
    );

    //just use the button ;)
    return (
        <button className={classes} ref={scrollElementRef} onClick={handleClick}>
            <div>{type === 'past' ? i18n('menu.vysledky') : dayMoment.format('dddd')}</div>
            <div>{formatFilterDate(dayMoment)}</div>
        </button>
    );
});

BettingDay.propTypes = {
    selected: PropTypes.bool,
    type: PropTypes.oneOf(['past', 'future']),
    byDate: PropTypes.string,
    dayMoment: PropTypes.instanceOf(moment),
    scrollElementRef: PropTypes.any
};

function formatFilterDate(date) {
    return date.calendar(null, FILTER_DATE_FORMATTING);
}

const FILTER_DATE_FORMATTING = {
    lastDay: `[${i18n('enum.calendar.vcera')}]`,
    sameDay: `[${i18n('jsp.homepage.datum.dnes')}]`,
    nextDay: `[${i18n('commons.datum.zajtra')}]`,
    lastWeek: 'DD.MM.',
    nextWeek: 'DD.MM.',
    sameElse: 'DD.MM.'
};
